// src/features/rrhh/horarios/api/horariosApi.js
//
// Conexión real con el backend NestJS.
// Endpoints base:
//   - Shifts:   /hr/employee-shifts
//   - Branches: /hr/employee-shifts/branches
//   - Areas:    /hr/employee-shifts/areas
//   - Sectors:  /hr/employee-shifts/sectors
import httpClient from "../../../../shared/api/httpClient";
import { qs } from "../../../../shared/api/qs";

// ── helpers ────────────────────────────────────────────────────

/** Normalise time to HH:mm (strip seconds if present) */
function hhmm(v) {
  if (!v) return v;
  return String(v).slice(0, 5);
}

/** Convert HH:mm time to minutes since midnight */
function toMin(hm) {
  if (!hm) return null;
  const [h, m] = String(hm).slice(0, 5).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function shiftDurationMinutes(shift) {
  const startMin = toMin(shift.startTime || shift.start_time);
  const endMin = toMin(shift.endTime || shift.end_time);

  if (startMin == null || endMin == null) return 0;

  const startOffset = Number(shift.startDayOffset ?? shift.start_day_offset ?? 0);
  const endOffset = Number(shift.endDayOffset ?? shift.end_day_offset ?? 0);
  const startAbs = startOffset * 24 * 60 + startMin;
  let endAbs = endOffset * 24 * 60 + endMin;

  if (endAbs <= startAbs) {
    endAbs += 24 * 60;
  }

  return Math.max(0, endAbs - startAbs);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}
function fmtYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Compute the Monday (start-of-week) for a given YYYYMMn integer key.
 * YYYYMMn: year * 1000 + month * 10 + weekOfMonth.
 * Week-of-month n means days ((n-1)*7+1) .. (n*7).
 * Monday = shift the first day of that bucket to the previous Monday.
 */
function weekKeyToMonday(wk) {
  const num = Number(wk);
  if (!num || num < 1900011) return null;
  const weekOfMonth = num % 10;
  const month = Math.floor((num % 1000) / 10);
  const year = Math.floor(num / 1000);
  if (month < 1 || month > 12 || weekOfMonth < 1 || weekOfMonth > 5) return null;
  const firstDay = 1 + (weekOfMonth - 1) * 7;
  const d = new Date(year, month - 1, firstDay);
  const dow = d.getDay() || 7; // Mon=1 .. Sun=7
  d.setDate(d.getDate() - dow + 1); // back to Monday
  return d;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Read licenseId from localStorage (set by EmpresaProvider). */
function getLicenseId() {
  try {
    return Number(localStorage.getItem("sabi_empresa_id")) || 1;
  } catch {
    return 1;
  }
}

function normalizeSectorText(value) {
  const text = String(value ?? "").trim();
  return text || null;
}

function buildSectorPayload(body = {}) {
  const legacySectionId =
    body.sectionId !== null &&
    body.sectionId !== undefined &&
    body.sectionId !== "" &&
    Number.isFinite(Number(body.sectionId))
      ? Number(body.sectionId)
      : null;
  const explicitSectorId = body.sectorId ?? legacySectionId;
  const sectorId =
    explicitSectorId !== null && explicitSectorId !== "" && Number.isFinite(Number(explicitSectorId))
      ? Number(explicitSectorId)
      : null;

  const sectorName = normalizeSectorText(
    body.sectorName ??
      body.sectionName ??
      (sectorId == null ? body.section : null) ??
      (sectorId == null ? body.sectionId : null),
  );

  return {
    sectorId,
    sectorName: sectorId == null ? sectorName : null,
  };
}

function hasSectorPayload(body = {}) {
  return (
    Object.prototype.hasOwnProperty.call(body, "sectorId") ||
    Object.prototype.hasOwnProperty.call(body, "sectorName") ||
    Object.prototype.hasOwnProperty.call(body, "section") ||
    Object.prototype.hasOwnProperty.call(body, "sectionId")
  );
}

// ── GET shifts (list) + group by employee ──────────────────────

/**
 * Fetches shifts for a given week and groups them by employee.
 *
 * @param {{ licenseId: number, weekKey: number, branchId?: number, areaId?: number, sectorId?: number, sectorName?: string, search?: string }} params
 * @param {object} [opts]  – { signal }
 * @returns {Promise<{ weekKey: number, weekStart: string, weekEnd: string, employees: Array }>}
 */
export const getEmployeeShiftsWeek = async (params, opts) => {
  const {
    weekKey,
    search,
    branchId,
    areaId,
    section,
    sectorId,
    sectorName,
    employeeId,
    ...rest
  } = params;

  const licenseId = rest.licenseId || getLicenseId();

  // 1) Buscar empleados según pertenencia real: sucursal / área / rol / búsqueda.
  let employeeMap = {};
  let employeeLookupLoaded = true;

  try {
    const empSearchParams = { licenseId };

    if (branchId) empSearchParams.branchId = branchId;
    if (areaId) empSearchParams.areaId = areaId;

    if (search && search.trim()) {
      empSearchParams.search = search.trim();
    }

    const empData = await httpClient.get(
      `/hr/employee-shifts/employees${qs(empSearchParams)}`,
      opts,
    );

    const empList = Array.isArray(empData)
      ? empData
      : empData?.data || empData?.items || [];

    empList.forEach((e) => {
      employeeMap[String(e.id)] = {
        fullName: e.fullName || e.full_name || "",
        dni: e.dni || "",
        hireDate: e.hireDate || e.hire_date || null,
        branches: Array.isArray(e.branches) ? e.branches : [],
        areas: Array.isArray(e.areas) ? e.areas : [],
        roles: Array.isArray(e.roles) ? e.roles : [],
      };
    });
  } catch (err) {
    employeeLookupLoaded = false;
    console.warn("Could not fetch employees for schedule filters:", err);
  }

  // 2) Traer turnos de la semana.
  // Importante: NO mandamos branchId/areaId acá,
  // porque esos filtros son de empleados, no de turnos.
  // sector sí se manda porque es un campo propio del turno.
  const shifts = await httpClient.get(
    `/hr/employee-shifts${qs({
      licenseId,
      weekKey,
      ...(employeeId ? { employeeId } : {}),
      ...(sectorId ? { sectorId } : {}),
      ...(sectorName ? { sectorName } : {}),
      ...(section ? { section } : {}),
      ...(search && search.trim() ? { search: search.trim() } : {}),
    })}`,
    opts,
  );

  // 3) Calcular límites de semana.
  const monday = weekKeyToMonday(weekKey);
  const weekStart = monday ? fmtYMD(monday) : "";
  const weekEnd = monday ? fmtYMD(addDays(monday, 6)) : "";

  // 4) Agrupar turnos por empleado, conservando SIEMPRE sus áreas asignadas.
  const shiftArr = Array.isArray(shifts) ? shifts : [];
  const grouped = {};
  
  const buildEmployeeGroup = (empId) => {
    const info = employeeMap[empId] || {};
  
    return {
      employeeId: empId,
      fullName: info.fullName || `Empleado #${empId}`,
      dni: info.dni || "",
      hireDate: info.hireDate || null,
      branches: Array.isArray(info.branches) ? info.branches : [],
      areas: Array.isArray(info.areas) ? info.areas : [],
      roles: Array.isArray(info.roles) ? info.roles : [],
      shifts: [],
    };
  };
  
  const ensureGroupedEmployee = (empId) => {
    if (!grouped[empId]) {
      grouped[empId] = buildEmployeeGroup(empId);
      return grouped[empId];
    }
  
    // Defensa por si el empleado ya fue creado previamente sin metadata completa.
    const info = employeeMap[empId] || {};
  
    grouped[empId] = {
      ...grouped[empId],
      fullName: grouped[empId].fullName || info.fullName || `Empleado #${empId}`,
      dni: grouped[empId].dni || info.dni || "",
      hireDate: grouped[empId].hireDate || info.hireDate || null,
      branches:
        Array.isArray(grouped[empId].branches) && grouped[empId].branches.length
          ? grouped[empId].branches
          : Array.isArray(info.branches)
          ? info.branches
          : [],
      areas:
        Array.isArray(grouped[empId].areas) && grouped[empId].areas.length
          ? grouped[empId].areas
          : Array.isArray(info.areas)
          ? info.areas
          : [],
      roles:
        Array.isArray(grouped[empId].roles) && grouped[empId].roles.length
          ? grouped[empId].roles
          : Array.isArray(info.roles)
          ? info.roles
          : [],
      shifts: Array.isArray(grouped[empId].shifts) ? grouped[empId].shifts : [],
    };
  
    return grouped[empId];
  };
  
  shiftArr.forEach((sh) => {
    const empId = String(sh.employeeId || sh.employee_id);
  
    // Si pudimos cargar empleados, mostramos solo los empleados filtrados por pertenencia.
    if (employeeLookupLoaded && !employeeMap[empId]) {
      return;
    }
  
    const employeeGroup = ensureGroupedEmployee(empId);
  
    employeeGroup.shifts.push({
      id: sh.id,
      workDate: sh.workDate || sh.work_date,
      startTime: hhmm(sh.startTime || sh.start_time),
      endTime: hhmm(sh.endTime || sh.end_time),
      areaId: String(sh.areaId || sh.area_id),
      branchId: String(sh.branchId || sh.branch_id),
      roleId: sh.roleId ?? sh.role_id ?? null,
      roleName: sh.roleName ?? sh.role_name ?? sh.role?.name ?? null,
      role: sh.role ?? null,
      turnId: sh.turnId ?? sh.turn_id ?? sh.id,
      scheduleId: sh.scheduleId ?? sh.schedule_id ?? sh.id,
      scheduleStartTime: hhmm(sh.scheduleStartTime || sh.schedule_start_time || sh.startTime || sh.start_time),
      scheduleEndTime: hhmm(sh.scheduleEndTime || sh.schedule_end_time || sh.endTime || sh.end_time),
      startDayOffset: Number(sh.startDayOffset ?? sh.start_day_offset ?? 0),
      endDayOffset: Number(sh.endDayOffset ?? sh.end_day_offset ?? 0),
      scheduleStartDayOffset: Number(sh.scheduleStartDayOffset ?? sh.schedule_start_day_offset ?? 0),
      scheduleEndDayOffset: Number(sh.scheduleEndDayOffset ?? sh.schedule_end_day_offset ?? 0),
      sectorId: sh.sectorId ?? sh.sector_id ?? sh.sectionId ?? sh.section_id ?? null,
      sectorName: sh.sectorName ?? sh.sector_name ?? sh.sectionName ?? sh.section_name ?? sh.section ?? null,
      sector: sh.sector ?? null,
      section: sh.section ?? sh.sectorName ?? sh.sector_name ?? sh.sectionName ?? sh.section_name ?? null,
      sectionId: sh.sectorId ?? sh.sector_id ?? sh.sectionId ?? sh.section_id ?? "",
      durationMinutes: Number(sh.durationMinutes ?? sh.duration_minutes ?? shiftDurationMinutes(sh)) || 0,
      employeeTotalMinutes: Number(sh.employeeTotalMinutes ?? sh.employee_total_minutes ?? 0) || 0,
      employeeAreaTotalMinutes: Number(sh.employeeAreaTotalMinutes ?? sh.employee_area_total_minutes ?? 0) || 0,
    });
  });
  
  // 5) Incluir siempre empleados filtrados aunque no tengan horarios.
  // Esto también permite que un empleado aparezca en todas sus áreas asignadas.
  Object.keys(employeeMap).forEach((empId) => {
    ensureGroupedEmployee(empId);
  });

  // 6) Completar totales en minutos por empleado y por área.
  // El backend ya los envía en cada turno; este fallback mantiene compatibilidad
  // con mocks o entornos viejos que todavía no tengan esos campos.
  Object.values(grouped).forEach((emp) => {
    let totalMinutes = 0;
    const minutesByArea = {};
    const backendEmployeeTotal = (emp.shifts || []).find(
      (sh) => Number(sh.employeeTotalMinutes) > 0
    )?.employeeTotalMinutes;

    (emp.shifts || []).forEach((sh) => {
      const minutes = Number(sh.durationMinutes) || shiftDurationMinutes(sh);
      const areaId = String(sh.areaId || sh.area_id || "");
      const backendAreaTotal = Number(sh.employeeAreaTotalMinutes) || 0;

      totalMinutes += minutes;

      if (areaId) {
        minutesByArea[areaId] = Math.max(
          minutesByArea[areaId] || 0,
          backendAreaTotal || 0
        );

        if (!backendAreaTotal) {
          minutesByArea[areaId] += minutes;
        }
      }
    });

    emp.totalMinutes = Number(backendEmployeeTotal) || totalMinutes;
    emp.minutesByArea = minutesByArea;
  });

  const employees = Object.values(grouped);

  return { weekKey, weekStart, weekEnd, employees };
};

// ── Reference data (branches, areas) ───────────────────

export const getBranches = async (opts) => {
  const licenseId = getLicenseId();
  return httpClient.get(`/hr/employee-shifts/branches${qs({ licenseId })}`, opts);
};

export const getAreas = async (opts) => {
  const licenseId = getLicenseId();
  return httpClient.get(`/hr/employee-shifts/areas${qs({ licenseId })}`, opts);
};

export const getSectors = async (opts) => {
  const licenseId = getLicenseId();
  return httpClient.get(`/hr/employee-shifts/sectors${qs({ licenseId })}`, opts);
};

export const getEmployeeShiftOptions = async (employeeId, opts) => {
  const licenseId = getLicenseId();
  return httpClient.get(
    `/hr/employee-shifts/employee-options${qs({ licenseId, employeeId })}`,
    opts,
  );
};

// ── Duplicate week ─────────────────────────────────────────────

/**
 * @param {{ licenseId: number, sourceWeekKey: number, targetWeekKey: number, employeeId?: number, employeeIds?: number[] }} payload
 */
export const duplicarSemanaAnterior = async (payload, opts) => {
  return httpClient.post(`/hr/employee-shifts/duplicate-week`, payload, opts);
};

// ── Bulk update (patch) ────────────────────────────────────────

export const patchEmployeeShiftsBulk = async (body, opts) => {
  // The backend currently handles individual PATCH, not a bulk endpoint.
  // We emulate bulk by sending individual requests.
  const results = { updated: [], created: [], deleted: [] };

  const updates = body.updates || [];
  const creates = body.creates || [];
  const deletes = body.deletes || [];

  for (const u of updates) {
    const id = u.detalle_id || u.id;
    if (!id) continue;
    const res = await httpClient.patch(`/hr/employee-shifts/${id}`, {
      startTime: u.hora_entrada || u.startTime,
      endTime: u.hora_salida || u.endTime,
      ...(u.branchId != null ? { branchId: Number(u.branchId) } : {}),
      ...(u.areaId != null ? { areaId: Number(u.areaId) } : {}),
      ...(hasSectorPayload(u)
        ? buildSectorPayload(u)
        : {}),
    }, opts);
    results.updated.push(res);
  }

  for (const c of creates) {
    const res = await httpClient.post(`/hr/employee-shifts`, {
      licenseId: Number(c.licenseId || body.licenseId || getLicenseId()),
      employeeId: Number(c.employeeId || c.empleado_id),
      branchId: Number(c.branchId || c.branch_id),
      areaId: Number(c.areaId || c.area_id),
      ...buildSectorPayload(c),
      workDate: c.workDate || c.work_date,
      startTime: c.startTime || c.hora_entrada,
      endTime: c.endTime || c.hora_salida,
    }, opts);
    results.created.push(res);
  }

  const licenseId = body.licenseId || getLicenseId();
  for (const id of deletes) {
    await httpClient.delete(`/hr/employee-shifts/${id}${qs({ licenseId })}`, opts);
    results.deleted.push(id);
  }

  return { ok: true, ...results };
};

// ── Create from plan ───────────────────────────────────────────

export const crearHorariosDesdePlan = async (payload, opts) => {
  return httpClient.post(`/hr/employee-shifts/create-from-plan`, payload, opts);
};

// ── Shift detail (single) ──────────────────────────────────────

/**
 * GET /hr/employee-shifts/:shiftId — returns the raw shift entity.
 * The frontend expects resolved names (branchName, areaName, employeeName).
 * We enrich the response with the reference lists stored in HorariosPage state.
 *
 * For now, return the raw shift; HorariosPage already falls back
 * to the inline shift object when detail is missing resolved names.
 */
export const getShiftDetail = async (shiftId, opts) => {
  // The backend doesn't have a /detail sub-route; a plain GET by id isn't exposed.
  // Since the controller only has list / create / patch / delete,
  // we return null and let HorariosPage use the inline shift data it already has.
  // When the backend adds GET /:id/detail, uncomment the line below:
  // return httpClient.get(`/hr/employee-shifts/${shiftId}/detail`, opts);
  return null;
};

// ── Update shift ───────────────────────────────────────────────

/**
 * PATCH /hr/employee-shifts/:shiftId
 * @param {number|string} shiftId
 * @param {Object} body – { startTime, endTime, branchId, areaId, sectorId?, sectorName?, workDate? }
 */
export const updateShiftDetail = async (shiftId, body, opts) => {
  const mapped = {};
  if (body.startTime != null) mapped.startTime = body.startTime;
  if (body.endTime != null) mapped.endTime = body.endTime;
  if (body.branchId != null) mapped.branchId = Number(body.branchId);
  if (body.areaId != null) mapped.areaId = Number(body.areaId);
  if (body.roleId != null) mapped.roleId = Number(body.roleId);
  if (hasSectorPayload(body)) {
    Object.assign(mapped, buildSectorPayload(body));
  }
  if (body.workDate != null) mapped.workDate = body.workDate;
  if (body.employeeId != null) mapped.employeeId = Number(body.employeeId);
  if (body.turns != null) {
    mapped.turns = (body.turns || []).map((turn) => ({
      id: turn.id != null ? Number(turn.id) : null,
      startTime: turn.startTime,
      endTime: turn.endTime,
      startDayOffset: Number(turn.startDayOffset || 0),
      endDayOffset: Number(turn.endDayOffset || 0),
      branchId: Number(turn.branchId),
      areaId: Number(turn.areaId),
      roleId: Number(turn.roleId),
      ...buildSectorPayload(turn),
      ...(turn.notes ? { notes: turn.notes } : {}),
    }));
  }

  return httpClient.patch(`/hr/employee-shifts/${shiftId}`, mapped, opts);
};

// ── Create shift ───────────────────────────────────────────────

/**
 * POST /hr/employee-shifts
 * @param {Object} body – { employeeId, workDate, startTime, endTime, branchId, areaId, sectorId?, sectorName?, licenseId? }
 */
export const createShift = async (body, opts) => {
  const turns = Array.isArray(body.turns)
    ? body.turns.map((turn) => ({
        startTime: turn.startTime,
        endTime: turn.endTime,
        startDayOffset: Number(turn.startDayOffset || 0),
        endDayOffset: Number(turn.endDayOffset || 0),
        branchId: Number(turn.branchId),
        areaId: Number(turn.areaId),
        roleId: Number(turn.roleId),
        ...buildSectorPayload(turn),
        ...(turn.notes ? { notes: turn.notes } : {}),
      }))
    : undefined;
  const firstTurn = turns?.[0] || body;

  return httpClient.post(`/hr/employee-shifts`, {
    licenseId: Number(body.licenseId || getLicenseId()),
    employeeId: Number(body.employeeId),
    branchId: Number(firstTurn.branchId),
    areaId: Number(firstTurn.areaId),
    ...(firstTurn.roleId ? { roleId: Number(firstTurn.roleId) } : {}),
    ...buildSectorPayload(body),
    workDate: body.workDate,
    startTime: body.startTime,
    endTime: body.endTime,
    ...(turns ? { turns } : {}),
    ...(body.notes ? { notes: body.notes } : {}),
  }, opts);
};

// ── Create shift bulk ──────────────────────────────────────────

export const createShiftsBulk = async (body, opts) => {
  return httpClient.post(`/hr/employee-shifts/bulk-create`, {
    licenseId: Number(body.licenseId || getLicenseId()),
    items: (body.items || []).map((item) => ({
      employeeId: Number(item.employeeId),
      branchId: Number(item.branchId),
      areaId: Number(item.areaId),
      roleId: Number(item.roleId),
      ...buildSectorPayload(item),
      workDate: item.workDate,
      startTime: item.startTime,
      endTime: item.endTime,
      ...(item.notes ? { notes: item.notes } : {}),
    })),
  }, opts);
};

// ── Delete shift ───────────────────────────────────────────────

/**
 * DELETE /hr/employee-shifts/:shiftId?licenseId=X
 * @param {number|string} shiftId
 */
export const deleteShift = async (shiftId, opts) => {
  const licenseId = getLicenseId();
  return httpClient.delete(`/hr/employee-shifts/${shiftId}${qs({ licenseId })}`, opts);
};

function getLicenseName() {
  const directKeys = [
    "sabi_empresa_nombre",
    "sabi_empresa_name",
    "empresa_nombre",
    "empresa_name",
    "license_name",
  ];

  for (const key of directKeys) {
    try {
      const value = localStorage.getItem(key);
      if (value) return value;
    } catch {
      // ignore
    }
  }

  const jsonKeys = ["sabi_empresa", "empresa", "license"];

  for (const key of jsonKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      const name =
        parsed?.name ||
        parsed?.nombre ||
        parsed?.businessName ||
        parsed?.razonSocial;

      if (name) return name;
    } catch {
      // ignore
    }
  }

  return "Empresa";
}

function slugifyFileName(value) {
  return String(value || "empresa")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  a.remove();
  window.URL.revokeObjectURL(url);
}

export const exportEmployeeShiftsPdf = async (params = {}, opts) => {
  const licenseId = Number(params.licenseId || getLicenseId());
  const licenseName = params.licenseName || getLicenseName();

  const query = {
    licenseId,
    weekKey: params.weekKey,
    licenseName,
    ...(params.branchId ? { branchId: params.branchId } : {}),
    ...(params.areaId ? { areaId: params.areaId } : {}),
    ...(params.sectorId ? { sectorId: params.sectorId } : {}),
    ...(params.sectorName ? { sectorName: params.sectorName } : {}),
    ...(params.section ? { section: params.section } : {}),
    ...(params.employeeId ? { employeeId: params.employeeId } : {}),
    ...(params.search?.trim() ? { search: params.search.trim() } : {}),
  };

  const blob = await httpClient.getBlob(
    `/hr/employee-shifts/export-pdf${qs(query)}`,
    {
      ...(opts || {}),
      headers: {
        Accept: "application/pdf",
        ...((opts && opts.headers) || {}),
      },
    },
  );

  const safeLicenseName = slugifyFileName(licenseName);
  const fileName = `horarios-${safeLicenseName}-${params.weekKey}.pdf`;

  downloadBlob(blob, fileName);

  return {
    ok: true,
    fileName,
  };
};
